/**
*
* HTTP service with authentication support.
*
* Responsibilities:
* - Attach Authorization header with access token
* - Handle 401 Unauthorized responses
* - Retry requests for guest users after session refresh
*/
import { AxiosError, AxiosHeaders, Method } from "axios";
import { getAccessToken, isAuthenticated } from "@/common/services/Auth";
import { routes } from "@/common/utils/routes";
import sessionService from "@/common/services/session/sessionService";
import { HttpClient, ErrorResponseData, RequestHeaders } from "./HttpClient";

const RETRY_LIMIT = 3;

const UNAUTHORIZED_CODE = 401;

export default class AuthenticatedHttpClient extends HttpClient {

  public constructor (readonly url?: string) {
    super(url);
  }

  protected async getAccessToken(): Promise<string> {
    return await getAccessToken();
  }

  public async request<Response,Params>(
    method: Method,
    url: string,
    data?: Params,
    headers?: RequestHeaders,
    retry?: number
  ): Promise<Response> {
    const authenticated = await isAuthenticated();
    const token: string = await this.getAccessToken();

    const requestHeaders = AxiosHeaders.from(headers);
    requestHeaders.set("Authorization", `Bearer ${token}`);

    return super.request<Response,Params>(method, url, data, requestHeaders)
      .catch(async (reason: AxiosError<ErrorResponseData | undefined>) => {
        const requestRetry = retry || 1;
        const { response } = reason;

        const code = response?.data?.error?.code || "";
        const status = response?.status || 0;

        if (status === UNAUTHORIZED_CODE) {
          if (authenticated) {
            window.location.assign(routes.logout());
          } else if (requestRetry < RETRY_LIMIT) {
            console.debug(`Request failed with code=${code}, getting new guest token, retry=${retry}`);

            await sessionService.refreshSession();
            return this.request<Response,Params>(method, url, data, headers, requestRetry + 1);
          } else {
            console.debug(`Request to ${url} failed with code=${code}, not getting new guest token, retry=${retry}.`);
          }
        }

        return Promise.reject(reason);
      });
  }
}
